import {ChangeDetectorRef, Component, DestroyRef, DoCheck, inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ValidatorFn, Validators} from '@angular/forms';
import {rxSpinner, Spinner} from '@npck/libs/commons';
import {filter, Subscription} from 'rxjs';
import {DictionaryHolderService} from '../../domain/dictionary-holder.service';
import {
    createPhoneList,
    getPhoneInfoByPhoneNumber,
    kzMsisdnValidator,
    kzPhoneInfo,
    PhoneInfo,
    phoneMaskValidator,
} from '../../domain/phone-utils';

export const PHONE_MIN_LENGTH = 9;

@Component({
    selector: 'af-phone-number',
    templateUrl: 'phone-number.component.html',
    styleUrls: ['phone-number.component.scss'],
    standalone: false,
})
export class PhoneNumberComponent implements OnInit, OnChanges, DoCheck {
    @Input() predefinedValue: string;
    @Input() control: FormControl<string>;
    @Input() selectedPhoneInfoControl: FormControl<PhoneInfo>;
    @Input() label: string;
    @Input() widthClass = 'w-476';
    @Input() readOnly = false;
    @Input() disabled = false;
    @Input() strictValidation = true;
    Validators = Validators;
    Spinner = Spinner;
    selectedCountryCode: PhoneInfo;
    spinnerId = 'countries';
    phones: PhoneInfo[] = [];
    filteredPhones: PhoneInfo[] = [];
    isRequired = false;
    private readonly DEFAULT_PREFIX = '+7';
    private defaultCountry?: PhoneInfo;
    private manualCountryPick = false;
    private lastValue = '';
    private msisdnValidator!: ValidatorFn;
    private controlSubscription?: Subscription;
    private destroyRef = inject(DestroyRef);

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    ngOnInit(): void {
        this.msisdnValidator = this.strictValidation
            ? kzMsisdnValidator(() => this.selectedCountryCode?.prefix, () => this.isRequired)
            : phoneMaskValidator(() => this.selectedCountryCode, () => this.isRequired);
        if (!this.predefinedValue && this.control.value) {
            this.predefinedValue = this.control.value;
        }
        this.control.disable({emitEvent: false});
        this.updateIsRequired();
        this.subscribeToControl();

        this.dictionaryHolderService.getReference('country')
            .pipe(rxSpinner(this.spinnerId), takeUntilDestroyed(this.destroyRef))
            .subscribe(countries => {
                this.phones = createPhoneList(countries);
                this.filteredPhones = this.phones;
                if (!this.readOnly && !this.disabled) {
                    this.control.enable();
                }
                let phoneInfo: PhoneInfo;
                if (!!this.predefinedValue && this.predefinedValue.length >= PHONE_MIN_LENGTH) {
                    phoneInfo = getPhoneInfoByPhoneNumber(this.predefinedValue, this.phones);
                }
                this.filteredPhones = this.phones;
                this.onCountryCodeSelected(phoneInfo || kzPhoneInfo, true);
                if (this.predefinedValue) {
                    this.control.setValue(this.predefinedValue, {emitEvent: false});
                }
                this.defaultCountry = this.phones.find(p => p.prefix === this.DEFAULT_PREFIX) ?? this.phones[0];
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.control && !changes.control.isFirstChange()) {
            this.subscribeToControl();
            this.updateIsRequired();
        }
    }

    ngDoCheck(): void {
        this.updateIsRequired();
    }

    onCountryCodeSelected(phone: PhoneInfo, silent = false): void {
        this.selectedCountryCode = phone;
        this.manualCountryPick = !silent;
        const value = silent ? this.predefinedValue : (phone.prefix || this.predefinedValue);
        if (value) {
            this.control.setValue(value, {emitEvent: !silent});
        }
        this.selectedPhoneInfoControl?.setValue(this.selectedCountryCode);
        this.updateIsRequired();
        if (silent) {
            this.filteredPhones = this.phones;
            this.cdr.detectChanges();
        }
    }

    private subscribeToControl(): void {
        this.controlSubscription?.unsubscribe();
        this.controlSubscription = new Subscription();
        this.controlSubscription.add(this.control.statusChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateIsRequired()));
        this.controlSubscription.add(this.control.valueChanges
            .pipe(filter(value => value !== null && value !== undefined), takeUntilDestroyed(this.destroyRef))
            .subscribe(value => this.handleControlValue(value)));
    }

    private handleControlValue(value: string): void {
        this.ensureFormatValidator();
        if (this.handleManualAndInitSelection(value)) {
            this.lastValue = value;
            return;
        }
        this.updateFilteredPhones(value);
        const normalizedPhone = value.startsWith('+') ? value : `+${value}`;
        const digitsOnly = value.replace(/\D/g, '');
        const isFirstKeystrokeIntoEmptyField = !this.lastValue && !value.startsWith('+')
            && digitsOnly.length > 0 && digitsOnly.length <= 2;
        if (isFirstKeystrokeIntoEmptyField && this.selectedCountryCode) {
            const next = this.selectedCountryCode.prefix + digitsOnly;
            this.lastValue = next;
            this.control.setValue(next, {emitEvent: false});
            this.updateFilteredPhones(next);
            this.cdr.detectChanges();
            return;
        }
        const match = this.phones.find(p => normalizedPhone.startsWith(p.prefix));
        if (match) {
            this.selectedCountryCode = match;
            this.selectedPhoneInfoControl?.setValue(this.selectedCountryCode);
            this.manualCountryPick = false;
        } else if (
            value.startsWith('0') ||
            (!value.startsWith('+') && digitsOnly.length > 3)
        ) {
            this.applyDefaultPrefixIfUnknown(normalizedPhone);
        }
        this.lastValue = this.control.value ?? '';
        this.cdr.detectChanges();
    }

    private ensureFormatValidator(): void {
        if (!this.control.hasValidator(this.msisdnValidator)) {
            this.control.addValidators(this.msisdnValidator);
            this.control.updateValueAndValidity({emitEvent: false});
        }
    }

    private updateIsRequired(): void {
        this.ensureFormatValidator();
        const requiredNow = this.control.hasValidator(Validators.required);
        if (this.isRequired !== requiredNow) {
            this.isRequired = requiredNow;
            this.control.updateValueAndValidity({emitEvent: false});
            this.cdr.detectChanges();
        }
    }

    private applyDefaultPrefixIfUnknown(withPlus: string): void {
        const digitsRaw = withPlus.replace(/^\+/, '').replace(/\D/g, '');
        if (digitsRaw === '') {
            return;
        }
        const digits = digitsRaw.replace(/^0+/, '');
        if (!digits) {
            return;
        }
        const next = this.DEFAULT_PREFIX + digits;
        if (next !== withPlus) {
            this.control.setValue(next, {emitEvent: false});
            this.updateFilteredPhones(next);
            this.cdr.detectChanges();
        }
        if (this.defaultCountry) {
            this.selectedCountryCode = this.defaultCountry;
            this.selectedPhoneInfoControl?.setValue(this.selectedCountryCode);
        }
    }

    private handleManualAndInitSelection(value: string): boolean {
        const normalizedPhone = value.startsWith('+') ? value : `+${value}`;
        if (
            this.selectedCountryCode?.prefix &&
            value === this.selectedCountryCode.prefix &&
            !this.manualCountryPick
        ) {
            this.filteredPhones = this.phones;
            this.cdr.detectChanges();
            return true;
        }
        if (this.manualCountryPick && !normalizedPhone.startsWith(this.selectedCountryCode?.prefix ?? '')) {
            this.manualCountryPick = false;
        }
        if (this.manualCountryPick && normalizedPhone.startsWith(this.selectedCountryCode?.prefix ?? '')) {
            this.selectedPhoneInfoControl?.setValue(this.selectedCountryCode);
            this.cdr.detectChanges();
            return true;
        }
        return false;
    }

    private updateFilteredPhones(raw: string): void {
        const digits = (raw ?? '').trim().replace(/\D/g, '');
        if (digits.length === 0 || digits.length > 3) {
            this.filteredPhones = this.phones;
            return;
        }
        this.filteredPhones = this.phones.filter(p => p.prefix.slice(1).startsWith(digits));
    }
}
